import React, { useEffect, useMemo, useRef, useState } from "react";
import cn from "classnames";
import { useHistory } from "react-router-dom";
import Icon from "../../components/Icon";
import Modal from "../../components/Modal";
import { getCatalog } from "../../api/catalog.service";
import { createCampaign, previewSegment, quickCampaign } from "../../api/campaigns.service";
import { LANGS, emptyLangMap, resolveTrans, toTranslations } from "./shared";
import Preview from "./Editor/Preview";
import ImageDropField from "./Editor/ImageDropField";
import styles from "./QuickSend.module.sass";

const KINDS = [
  { value: "deck", label: "🀄 Nuevo deck", prefix: { es: "Nuevo deck", en: "New deck", fr: "Nouveau deck", pt: "Novo deck", ru: "Новая колода" } },
  { value: "text", label: "📖 Nueva historia", prefix: { es: "Nueva historia", en: "New story", fr: "Nouvelle histoire", pt: "Nova história", ru: "Новая история" } },
  { value: "free", label: "✍️ Libre" },
];

const CHANNELS_QS = [
  { value: "push", label: "Push" },
  { value: "inapp", label: "In-App" },
];

// Nombre de un item del catálogo en un idioma (titleTranslations → title → '').
const itemName = (item, lang) => {
  const fromTranslations =
    resolveTrans(item?.titleTranslations, lang) ||
    (Array.isArray(item?.title) ? resolveTrans(item.title, lang) : "");
  return fromTranslations || (typeof item?.title === "string" ? item.title : "");
};

/**
 * Anuncio rápido: "ha salido un deck/historia nuevo" → push o in-app a todo el mundo
 * en dos clics. Autorrellena los textos en todos los idiomas desde las traducciones
 * del propio contenido y usa POST /messaging/campaigns/quick (crear + enviar).
 */
const QuickSend = ({ visible, onClose, onSent, onNotify }) => {
  const history = useHistory();
  const [kind, setKind] = useState("deck");
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [channels, setChannels] = useState(["push", "inapp"]);
  const [audienceLangs, setAudienceLangs] = useState([]);
  const [lang, setLang] = useState("es");
  const [titles, setTitles] = useState(emptyLangMap());
  const [bodies, setBodies] = useState(emptyLangMap());
  const [imageUrl, setImageUrl] = useState("");
  const [count, setCount] = useState(null);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const confirmTimer = useRef(null);

  // Catálogo de decks/historias para elegir.
  useEffect(() => {
    if (!visible || kind === "free") return;
    setLoadingItems(true);
    getCatalog({ type: kind === "deck" ? "deck" : "text", take: 500 })
      .then((resp) => {
        const list = resp?.data?.results || resp?.data || [];
        setItems(
          (Array.isArray(list) ? list : []).filter(
            (it) => itemName(it, "en") || itemName(it, "es")
          )
        );
      })
      .catch(() => setItems([]))
      .finally(() => setLoadingItems(false));
  }, [visible, kind]);

  // Conteo de audiencia (todos, con filtro de idioma opcional).
  useEffect(() => {
    if (!visible) return;
    setCount(null);
    previewSegment(audienceLangs.length ? { langs: audienceLangs } : {})
      .then((resp) => setCount(resp?.data?.targeted ?? null))
      .catch(() => setCount(null));
  }, [visible, audienceLangs]);

  useEffect(() => () => confirmTimer.current && clearTimeout(confirmTimer.current), []);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? items.filter((it) =>
          `${itemName(it, "en")} ${itemName(it, "es")}`.toLowerCase().includes(q)
        )
      : items;
    return base.slice(0, 30);
  }, [items, search]);

  const reset = () => {
    setSelected(null);
    setTitles(emptyLangMap());
    setBodies(emptyLangMap());
    setImageUrl("");
    setSearch("");
    setConfirming(false);
  };

  const pick = (item) => {
    setSelected(item);
    const kindMeta = KINDS.find((k) => k.value === kind);
    const nextTitles = emptyLangMap();
    const nextBodies = emptyLangMap();
    LANGS.forEach((l) => {
      const name = itemName(item, l);
      if (name) {
        const prefix = kindMeta?.prefix?.[l] || kindMeta?.prefix?.en || "";
        nextTitles[l] = `${kind === "deck" ? "🀄" : "📖"} ${prefix}: ${name}`;
      }
      const desc =
        resolveTrans(item.descriptionTranslations, l) ||
        (Array.isArray(item.description) ? resolveTrans(item.description, l) : "");
      if (desc) nextBodies[l] = desc.length > 160 ? `${desc.slice(0, 157)}…` : desc;
    });
    setTitles(nextTitles);
    setBodies(nextBodies);
    const img = item.imageUrl && !String(item.imageUrl).includes("undefined") ? item.imageUrl : "";
    setImageUrl(img);
  };

  const canSend = toTranslations(titles).length > 0;

  const toggleChannel = (value) =>
    setChannels((prev) => {
      const next = prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value];
      return next.length ? next : prev;
    });

  // UNA campaña multi-canal (antes creaba una campaña por canal, duplicando historial).
  const buildPayload = () => ({
    name: `Anuncio: ${titles.en || titles.es || "sin título"}`,
    channels,
    category: "announcement",
    segment: audienceLangs.length ? { langs: audienceLangs } : {},
    title: toTranslations(titles),
    body: toTranslations(bodies),
    imageUrl: imageUrl || undefined,
  });

  const handleSend = async () => {
    if (!confirming) {
      setConfirming(true);
      confirmTimer.current = setTimeout(() => setConfirming(false), 4000);
      return;
    }
    setSending(true);
    try {
      const resp = await quickCampaign(buildPayload());
      if (typeof resp?.data?.targeted === "number") {
        onNotify?.({
          severity: "success",
          msg: `Anuncio enviado a ${resp.data.targeted.toLocaleString("es-ES")} usuarios`,
        });
        reset();
        onClose();
        onSent?.();
      } else {
        onNotify?.({ severity: "error", msg: "No se pudo enviar el anuncio" });
      }
    } catch (e) {
      onNotify?.({ severity: "error", msg: "Error al enviar el anuncio" });
    } finally {
      setSending(false);
      setConfirming(false);
    }
  };

  const handleOpenInEditor = async () => {
    try {
      const resp = await createCampaign(buildPayload());
      if (resp?.data?._id) {
        history.push(`/campaigns/${resp.data._id}`);
      } else {
        onNotify?.({ severity: "error", msg: "No se pudo crear el borrador" });
      }
    } catch (e) {
      onNotify?.({ severity: "error", msg: "No se pudo crear el borrador" });
    }
  };

  return (
    <Modal visible={visible} onClose={() => (sending ? null : onClose())} outerClassName={styles.modal}>
      <div className={styles.quick}>
        <div className={cn("title-blue", styles.title)}>Anuncio rápido</div>
        <div className={styles.subtitle}>
          Avisa a todo el mundo de un deck o historia nuevos. Sin borradores: se envía ya.
        </div>

        <div className={styles.kinds}>
          {KINDS.map((k) => (
            <button
              key={k.value}
              type="button"
              className={cn(styles.kind, { [styles.kindActive]: kind === k.value })}
              onClick={() => {
                setKind(k.value);
                reset();
              }}
            >
              {k.label}
            </button>
          ))}
          <div className={styles.channels}>
            {CHANNELS_QS.map((c) => (
              <button
                key={c.value}
                type="button"
                className={cn(styles.chip, { [styles.chipActive]: channels.includes(c.value) })}
                onClick={() => toggleChannel(c.value)}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.grid}>
          <div className={styles.left}>
            {kind !== "free" && !selected && (
              <>
                <div className={styles.search}>
                  <Icon name="search" size="18" />
                  <input
                    autoFocus
                    placeholder={kind === "deck" ? "Busca el deck…" : "Busca la historia…"}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className={styles.items}>
                  {loadingItems ? (
                    <div className={styles.itemsEmpty}>Cargando…</div>
                  ) : filteredItems.length === 0 ? (
                    <div className={styles.itemsEmpty}>Sin resultados</div>
                  ) : (
                    filteredItems.map((it) => (
                      <button
                        key={it.sourceId || it._id}
                        type="button"
                        className={styles.item}
                        onClick={() => pick(it)}
                      >
                        {it.imageUrl && !String(it.imageUrl).includes("undefined") ? (
                          <img src={it.imageUrl} alt="" />
                        ) : (
                          <span className={styles.itemFallback}>
                            {it.emoji || (kind === "deck" ? "🀄" : "📖")}
                          </span>
                        )}
                        <span className={styles.itemName}>
                          {itemName(it, "es") || itemName(it, "en")}
                        </span>
                        <Icon name="arrow-right" size="16" />
                      </button>
                    ))
                  )}
                </div>
              </>
            )}

            {(kind === "free" || selected) && (
              <>
                {selected && (
                  <button type="button" className={styles.changeItem} onClick={() => setSelected(null)}>
                    <Icon name="arrow-left" size="14" />
                    {itemName(selected, "es") || itemName(selected, "en")}
                    <span>cambiar</span>
                  </button>
                )}

                <div className={styles.langTabs}>
                  {LANGS.map((l) => (
                    <button
                      key={l}
                      type="button"
                      className={cn(styles.langTab, { [styles.langTabActive]: lang === l })}
                      onClick={() => setLang(l)}
                    >
                      {l.toUpperCase()}
                      <span className={cn(styles.langDot, { [styles.langDotOk]: !!titles[l] })} />
                    </button>
                  ))}
                </div>
                <input
                  className={styles.input}
                  placeholder={`Título (${lang.toUpperCase()})`}
                  value={titles[lang]}
                  onChange={(e) => setTitles({ ...titles, [lang]: e.target.value })}
                />
                <textarea
                  className={cn(styles.input, styles.textarea)}
                  rows={2}
                  placeholder={`Cuerpo (${lang.toUpperCase()}) — opcional`}
                  value={bodies[lang]}
                  onChange={(e) => setBodies({ ...bodies, [lang]: e.target.value })}
                />
                <ImageDropField value={imageUrl} onChange={setImageUrl} onNotify={onNotify} />

                <div className={styles.audience}>
                  <span className={styles.audienceLabel}>Audiencia: todos</span>
                  {LANGS.map((l) => (
                    <button
                      key={l}
                      type="button"
                      className={cn(styles.chip, {
                        [styles.chipActive]: audienceLangs.includes(l),
                      })}
                      onClick={() =>
                        setAudienceLangs((prev) =>
                          prev.includes(l) ? prev.filter((x) => x !== l) : [...prev, l]
                        )
                      }
                    >
                      {l.toUpperCase()}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {(kind === "free" || selected) && (
            <div className={styles.right}>
              <Preview
                channel={channels[0]}
                lang={lang}
                title={titles[lang] || titles.en || ""}
                body={bodies[lang] || bodies.en || ""}
                imageUrl={imageUrl}
              />
            </div>
          )}
        </div>

        {(kind === "free" || selected) && (
          <div className={styles.footer}>
            <button type="button" className="button-stroke" onClick={handleOpenInEditor} disabled={!canSend || sending}>
              Ajustar en editor
            </button>
            <button
              type="button"
              className={cn("button", { [styles.confirming]: confirming })}
              onClick={handleSend}
              disabled={!canSend || sending}
            >
              {sending
                ? "Enviando…"
                : confirming
                ? `¿Seguro? Enviar a ≈ ${count == null ? "…" : count.toLocaleString("es-ES")}`
                : `Enviar ahora a ≈ ${count == null ? "…" : count.toLocaleString("es-ES")}`}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default QuickSend;
